import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Typography } from 'components/ui';
import { WhiteCard } from 'components/styles_components/card';
import { CardTitleTypography } from 'components/styles_components/typography';
import { useError } from 'contexts/error';
import { endOfMonth, startOfMonth, subMonths, format } from 'date-fns';
import { Line } from 'react-chartjs-2';
import { FormattedMessage, useIntl } from 'react-intl';
import InspectionMonthlyOverviewQuery from 'queries/inspection/monthly_overview';
import LoaderContainer from 'components/layout/loader_container';
import theme from 'theme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const withAlpha = (hex, alpha) => {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return /^#[0-9a-fA-F]{8}$/.test(hex) ? `${hex.slice(0, 7)}${a}` : `${hex}${a}`;
};

const MonthlyOverview = () => {
  const setError = useError();
  const intl = useIntl();

  const fromDate = useMemo(() => {
    return format(startOfMonth(subMonths(new Date(), 11)), 'yyyy-MM-dd');
  }, []);

  const toDate = useMemo(() => {
    return format(endOfMonth(new Date()), 'yyyy-MM-dd');
  }, []);

  const {
    data = { inspectionMonthlyOverview: { overview: [] } },
    loading,
    error,
  } = useQuery(InspectionMonthlyOverviewQuery, {
    variables: {
      fromDate,
      toDate,
    },
    onError: () => {
      setError('error.inspections.monthly_overview');
    },
  });

  const chartData = useMemo(() => {
    if (!data?.inspectionMonthlyOverview?.overview) {
      return [];
    }

    const overview = data.inspectionMonthlyOverview.overview;

    const monthNames = [
      intl.formatMessage({ id: 'month.january' }),
      intl.formatMessage({ id: 'month.february' }),
      intl.formatMessage({ id: 'month.march' }),
      intl.formatMessage({ id: 'month.april' }),
      intl.formatMessage({ id: 'month.may' }),
      intl.formatMessage({ id: 'month.june' }),
      intl.formatMessage({ id: 'month.july' }),
      intl.formatMessage({ id: 'month.august' }),
      intl.formatMessage({ id: 'month.september' }),
      intl.formatMessage({ id: 'month.october' }),
      intl.formatMessage({ id: 'month.november' }),
      intl.formatMessage({ id: 'month.december' }),
    ];

    return overview.map((item) => ({
      month: monthNames[item.month - 1],
      successful: item.successfulResults,
      unsuccessful: item.unsuccessfulResults,
    }));
  }, [data, intl]);

  const chartConfig = useMemo(() => {
    const axisLabel = intl.formatMessage({ id: 'chart.axis.number_of_inspections' });
    return {
      data: {
        labels: chartData.map((item) => item.month),
        datasets: [
          {
            label: intl.formatMessage({ id: 'chart.legend.successful_results' }),
            data: chartData.map((item) => item.successful),
            borderColor: theme.palette.chart.successfulResults,
            backgroundColor: withAlpha(theme.palette.chart.successfulResults, 0.15),
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
          },
          {
            label: intl.formatMessage({ id: 'chart.legend.unsuccessful_results' }),
            data: chartData.map((item) => item.unsuccessful),
            borderColor: theme.palette.chart.unsuccessfulResults,
            backgroundColor: withAlpha(theme.palette.chart.unsuccessfulResults, 0.15),
            tension: 0.3,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            position: 'top',
            align: 'end',
          },
          tooltip: {
            mode: 'index',
            intersect: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { stepSize: 1, precision: 0 },
            title: {
              display: true,
              text: axisLabel,
            },
          },
        },
      },
    };
  }, [chartData, intl]);

  return (
    <WhiteCard>
      <LoaderContainer loading={loading} error={error} wrap={false}>
        <CardTitleTypography variant="h5">
          <FormattedMessage id="chart.monthly_overview" />
        </CardTitleTypography>
        {chartData.length === 0 ||
        chartData.every((item) => item.successful === 0 && item.unsuccessful === 0) ? (
          <Typography variant="body1" align="center" sx={{ py: 4 }}>
            <FormattedMessage id="chart.no_data" />
          </Typography>
        ) : (
          <div style={{ height: 340 }}>
            <Line data={chartConfig.data} options={chartConfig.options} />
          </div>
        )}
      </LoaderContainer>
    </WhiteCard>
  );
};

export default MonthlyOverview;
